import { Search, Plus, ChevronLeft, ChevronRight, Eraser, Filter, Wand2, Loader2 } from 'lucide-react';
import { AnimatedIconButton } from '../../../common/AnimatedIconButton';
import { SortSelector, SortType } from '../../../common/SortSelector';
import { DateFilterSelector } from '../../../common/DateFilterSelector';
import { StatusFilterDropdown } from './StatusFilterDropdown';
import { PostStatusFilter } from '../../../../utils/Constants';
import { Input } from '../../../ui/input';
import type { CommunicationChannel, ChannelType } from '../../../../types';
import { defaultChannels } from '../../../../config/channels';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { useGenerationContext } from '../../../../contexts/GenerationContext';
import { channelIcons, ChannelSvgIcon, channelSvgIcons } from '../../../../utils/Constants';
import { SpaceSwitcher } from '../../../common/SpaceSwitcher';
import { useAuth } from '../../../../hooks/useAuth';

interface PostGalleryHeaderProps {
  isMobile: boolean;
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  sortType: SortType;
  sortOrder: 'asc' | 'desc';
  setSortType: (type: SortType) => void;
  setSortOrder: (order: 'asc' | 'desc') => void;
  statusFilter: PostStatusFilter;
  setStatusFilter: (status: PostStatusFilter) => void;
  setIsCreatingNewPost: (isCreating: boolean) => void;
  onOpenGenerateModal: () => void;
  setShowClearGalleryConfirm: (show: boolean) => void;
  showExternal: boolean;
  handleToggleExternal: () => void;
  showInternal: boolean;
  handleToggleInternal: () => void;
  // V4-POSTS-CREATED-BY-USER-ID 2026-07-29 — filtre « mes posts ».
  // `canFilterMine` est faux tant qu'aucun utilisateur n'est identifié : on
  // masque alors le contrôle plutôt que d'exposer un bouton qui viderait la galerie.
  showOnlyMine: boolean;
  handleToggleOnlyMine: () => void;
  canFilterMine: boolean;
  scrollChannels: (direction: 'left' | 'right') => void;
  channelOffset: number;
  filteredAvailableChannels: CommunicationChannel[];
  visibleChannelsCount: number;
  visibleChannels: CommunicationChannel[];
  selectedChannelIds: Set<string>;
  toggleChannel: (id: string) => void;
  startDate: string;
  endDate: string;
  setStartDate: (date: string) => void;
  setEndDate: (date: string) => void;
  isSetupComplete?: boolean;
}

export function PostGalleryHeader({
  isMobile,
  showFilters,
  setShowFilters,
  searchQuery,
  setSearchQuery,
  sortType,
  sortOrder,
  setSortType,
  setSortOrder,
  statusFilter,
  setStatusFilter,
  setIsCreatingNewPost,
  onOpenGenerateModal,
  setShowClearGalleryConfirm,
  showExternal,
  handleToggleExternal,
  showInternal,
  showOnlyMine,
  handleToggleOnlyMine,
  canFilterMine,
  handleToggleInternal,
  scrollChannels,
  channelOffset,
  filteredAvailableChannels,
  visibleChannelsCount,
  visibleChannels,
  selectedChannelIds,
  toggleChannel,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  isSetupComplete = true
}: PostGalleryHeaderProps) {
  const { t } = useLanguage();
  const { isGenerating, isGeneratingBatch } = useGenerationContext();
  // Lecture seule (guest / viewer d'agence) : pas de création/génération/effacement.
  // currentSpace : pour afficher le nom de l'espace courant dans le placeholder de recherche.
  const { isReadOnly, currentSpace } = useAuth();

  const getChannelIcon = (channel: CommunicationChannel): string => {
    const icons: Record<ChannelType | string, string> = {
      'X': 'X',
      'Twitter': 'X', // Alias for backwards compatibility
      'Facebook': 'f',
      'Instagram': 'IG',
      'LinkedIn': 'in',
      'TikTok': '♪',
      'YouTube': '▶', 'Newsletter': '✉', 'Slack': 'S', 'WhatsApp': 'W', 'Telegram': 'T', 'Teams': 'M', 'Discord': 'D',
      'Pinterest': 'P',
      'Baidu': 'B',
    };
    return icons[channel.network] || (channel.network || '?').charAt(0);
  };

  const getChannelLabel = (channel: CommunicationChannel): string => {
    if (channel.customName) {
      return channel.customName;
    }
    const config = defaultChannels.find(c => c.name === channel.network);
    return config?.displayName || channel.network;
  };

  // Sur mobile, le header desktop est masqué : c'est `MobilePostGalleryHeader`
  // (toggle via le bouton "Grid" central de `MobileBottomBar`) qui sert de
  // source de vérité pour search/filter/sort/create/generate/clear.
  // Voir `src/components/features/posts/MobilePostGalleryHeader.tsx`.
  if (isMobile) return null;

  return (
    <div 
      className="absolute top-4 left-4 right-4 z-10 flex-shrink-0" 
      style={{ 
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        backgroundColor: 'rgba(var(--color-background-rgb), 0.7)',
        border: `1px solid var(--color-border)`,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1), 0 2px 8px rgba(0, 0, 0, 0.05)',
        borderRadius: '30px'
      }}
    >
      <div className="px-3 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1">
            <AnimatedIconButton
              icon={Filter}
              label={t.posts.filters}
              onClick={() => setShowFilters(!showFilters)}
              variant="secondary"
            />

            {/* Dropdown filtre statut — entre le bouton Filter et la search bar.
                Permet de filtrer Tous / À publier / Programmés / Publiés. */}
            <StatusFilterDropdown value={statusFilter} onChange={setStatusFilter} />

            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: 'var(--color-text-muted)' }} />
              <Input
                type="text"
                placeholder={currentSpace?.name
                  ? t.posts.searchPlaceholderInSpace.replace('{space}', currentSpace.name)
                  : t.posts.searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="rounded-full h-9 text-sm w-full"
                style={{
                  backgroundColor: 'var(--color-background)',
                  color: 'var(--color-text-primary)',
                  border: `1px solid var(--color-border)`,
                  paddingLeft: '2.25rem',
                }}
              />
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <SortSelector
              sortType={sortType}
              sortOrder={sortOrder}
              onSortTypeChange={setSortType}
              onSortOrderChange={setSortOrder}
            />
            {/* Actions d'écriture masquées en lecture seule (guest / viewer) */}
            {!isReadOnly && (
              <>
                {/* Bouton "Créer un post" individuel — reste accessible même pendant une génération groupée */}
                <div title={!isSetupComplete ? (t.posts.setupIncompleteTooltip ?? 'Finalisez la configuration initiale pour accéder à cette fonctionnalité') : undefined}>
                  <AnimatedIconButton
                    icon={Plus}
                    label={t.posts.createPost}
                    onClick={() => setIsCreatingNewPost(true)}
                    variant="default"
                    disabled={!isSetupComplete}
                  />
                </div>
                {/* Bouton "Générer des posts" — reste cliquable pendant une génération groupée pour permettre d'en relancer */}
                {/* V4-ONBOARDING-TOUR 2026-06-18 — cible du tour produit (data-tour). */}
                <div data-tour="generate" title={!isSetupComplete ? (t.posts.setupIncompleteTooltip ?? 'Finalisez la configuration initiale pour accéder à cette fonctionnalité') : undefined}>
                  <AnimatedIconButton
                    icon={isGeneratingBatch ? Loader2 : Wand2}
                    label={isGeneratingBatch ? t.toasts.generating : t.posts.generatePosts}
                    onClick={onOpenGenerateModal}
                    variant="default"
                    iconClassName={isGeneratingBatch ? 'animate-spin' : ''}
                    disabled={!isSetupComplete}
                    // Action principale : label visible en permanence (pas seulement
                    // au survol) une fois le setup terminé, pour que l'utilisateur
                    // sache immédiatement où générer du contenu. Retour test user.
                    revealLabelOnHover={false}
                  />
                </div>
                <div title={!isSetupComplete ? (t.posts.setupIncompleteTooltip ?? 'Finalisez la configuration initiale pour accéder à cette fonctionnalité') : undefined}>
                  <AnimatedIconButton
                    icon={Eraser}
                    label={t.posts.clear}
                    onClick={() => setShowClearGalleryConfirm(true)}
                    variant="danger"
                    disabled={isGenerating || !isSetupComplete}
                  />
                </div>
              </>
            )}
            <SpaceSwitcher />
          </div>
        </div>
        
        {showFilters && (
          <div 
            className="mt-3 pt-3" 
            style={{ borderTop: '1px solid var(--color-border)' }}
          >
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-3 flex-shrink-0">
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={handleToggleExternal}
                    className={`w-3 h-3 rounded-full transition-all ${
                      showExternal 
                        ? '' 
                        : 'border bg-transparent'
                    }`}
                    style={showExternal ? {
                      backgroundColor: 'var(--color-primary)',
                      boxShadow: '0 0 8px rgba(85, 0, 255, 0.6)'
                    } : {
                      borderColor: 'var(--color-accent)'
                    }}
                    title={t.posts.external}
                  />
                  <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{t.posts.external}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={handleToggleInternal}
                    className={`w-3 h-3 rounded-full transition-all ${
                      showInternal 
                        ? '' 
                        : 'border bg-transparent'
                    }`}
                    style={showInternal ? {
                      backgroundColor: 'var(--color-primary)',
                      boxShadow: '0 0 8px rgba(85, 0, 255, 0.6)'
                    } : {
                      borderColor: 'var(--color-accent)'
                    }}
                    title={t.posts.internal}
                  />
                  <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{t.posts.internal}</span>
                </div>
                {/* V4-POSTS-CREATED-BY-USER-ID 2026-07-29 — « mes posts ».
                    Même pastille que Externe/Interne pour rester homogène. */}
                {canFilterMine && (
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={handleToggleOnlyMine}
                      className={`w-3 h-3 rounded-full transition-all ${
                        showOnlyMine
                          ? ''
                          : 'border bg-transparent'
                      }`}
                      style={showOnlyMine ? {
                        backgroundColor: 'var(--color-primary)',
                        boxShadow: '0 0 8px rgba(85, 0, 255, 0.6)'
                      } : {
                        borderColor: 'var(--color-accent)'
                      }}
                      title={t.posts.onlyMineHint}
                      aria-pressed={showOnlyMine}
                    />
                    <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>{t.posts.onlyMine}</span>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2 flex-1 min-w-0">
                <button
                  onClick={() => scrollChannels('left')}
                  disabled={channelOffset === 0}
                  className="p-1 rounded disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0 transition-colors"
                  style={{ backgroundColor: 'transparent' }}
                  onMouseEnter={(e) => {
                    if (channelOffset !== 0) {
                      e.currentTarget.style.backgroundColor = 'var(--color-surface-elevated)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <ChevronLeft className="w-4 h-4" style={{ color: 'var(--color-text-primary)' }} />
                </button>
                
                <div className="flex items-center gap-3 flex-1 justify-between">
                  {visibleChannels.map((channel) => {
                    const networkConfig = defaultChannels.find(c => c.name === channel.network);
                    const isActive = selectedChannelIds.has(channel.id);
                    
                    return (
                      <button
                        key={channel.id}
                        onClick={() => toggleChannel(channel.id)}
                        className="flex items-center gap-2 cursor-pointer group transition-opacity flex-1 min-w-0"
                      >
                        <ChannelSvgIcon
                          network={channel.network}
                          size={28}
                          className={!isActive ? 'opacity-30' : ''}
                          fallbackClassName={isActive ? `${networkConfig?.color} ${networkConfig?.textColor}` : 'border'}
                          fallbackStyle={!isActive ? { borderColor: 'var(--color-accent)', color: 'var(--color-accent)' } : undefined}
                        />
                        <span className={`text-sm font-medium truncate ${isActive ? '' : 'opacity-30'}`} style={{ color: 'var(--color-text-primary)' }}>
                          {getChannelLabel(channel)}
                        </span>
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => scrollChannels('right')}
                  disabled={channelOffset >= filteredAvailableChannels.length - visibleChannelsCount}
                  className="p-1 rounded disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0 transition-colors"
                  style={{ backgroundColor: 'transparent' }}
                  onMouseEnter={(e) => {
                    if (channelOffset < filteredAvailableChannels.length - visibleChannelsCount) {
                      e.currentTarget.style.backgroundColor = 'var(--color-surface-elevated)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  <ChevronRight className="w-4 h-4" style={{ color: 'var(--color-text-primary)' }} />
                </button>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                <DateFilterSelector
                  startDate={startDate}
                  endDate={endDate}
                  onStartDateChange={setStartDate}
                  onEndDateChange={setEndDate}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
